import React, { useEffect, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Space,
  Upload,
  InputNumber,
  Image,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import ProjectGalleryService from "../../../services/ProjectGalleryService";

const ProjectGallery = () => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewing, setViewing] = useState(null);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [beforeFile, setBeforeFile] = useState(null);
  const [afterFile, setAfterFile] = useState(null);
  const [beforePreview, setBeforePreview] = useState(null);
  const [afterPreview, setAfterPreview] = useState(null);
  const [form] = Form.useForm();

  const fetch = async () => {
    setLoading(true);
    try {
      const res = await ProjectGalleryService.getProjectGallery();
      setList(res.data || []);
    } catch (err) {
      console.error(err);
      message.error("Failed to load project gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetch();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setBeforeFile(null);
    setAfterFile(null);
    setBeforePreview(null);
    setAfterPreview(null);
    setIsModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue({
      title: record.title,
      order: record.order,
      language: record.language || "Marathi",
    });
    setBeforeFile(null);
    setAfterFile(null);
    setBeforePreview(record.before_image || null);
    setAfterPreview(record.after_image || null);
    setIsModalOpen(true);
  };

  const handleDelete = (id) => {
    Modal.confirm({
      title: "Delete gallery image",
      content: "Are you sure?",
      async onOk() {
        try {
          await ProjectGalleryService.deleteProjectGallery(id);
          message.success("Deleted");
          fetch();
        } catch (err) {
          console.error(err);
          message.error("Delete failed");
        }
      },
    });
  };

  const handleView = (record) => {
    setViewing(record);
    setIsViewOpen(true);
  };

  const validateImage = (file) => {
    const allowedTypes = ["image/jpeg", "image/png", "image/webp"];
    const maxSize = 5 * 1024 * 1024;
    if (!allowedTypes.includes(file.type)) {
      message.error("Invalid image type. Allowed: jpg, png, webp");
      return false;
    }
    if (file.size > maxSize) {
      message.error("Image must be smaller than 5MB");
      return false;
    }
    return true;
  };

  const beforeUploadBefore = (file) => {
    if (!validateImage(file)) return false;
    setBeforeFile(file);
    const url = URL.createObjectURL(file);
    setBeforePreview(url);
    return false;
  };

  const beforeUploadAfter = (file) => {
    if (!validateImage(file)) return false;
    setAfterFile(file);
    const url = URL.createObjectURL(file);
    setAfterPreview(url);
    return false;
  };

  const cleanupPreviews = () => {
    if (beforePreview && beforePreview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(beforePreview);
      } catch (e) {}
    }
    if (afterPreview && afterPreview.startsWith("blob:")) {
      try {
        URL.revokeObjectURL(afterPreview);
      } catch (e) {}
    }
  };

  const onFinish = async (values) => {
    // For creating, both images are mandatory
    if (!editing) {
      if (!beforeFile) {
        message.error("Please upload a Before image");
        return;
      }
      if (!afterFile) {
        message.error("Please upload an After image");
        return;
      }
    }

    const payload = {
      title: values.title || "",
      order: values.order || 0,
      language: values.language || "Marathi",
    };

    setLoading(true);
    try {
      if (editing) {
        await ProjectGalleryService.updateProjectGallery(
          editing.id,
          payload,
          beforeFile,
          afterFile
        );
        message.success("Updated");
      } else {
        await ProjectGalleryService.createProjectGallery(
          payload,
          beforeFile,
          afterFile
        );
        message.success("Created");
      }

      setIsModalOpen(false);
      form.resetFields();
      setBeforeFile(null);
      setAfterFile(null);
      cleanupPreviews();
      setBeforePreview(null);
      setAfterPreview(null);
      fetch();
    } catch (err) {
      console.error(err);
      message.error("Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    form.resetFields();
    setBeforeFile(null);
    setAfterFile(null);
    cleanupPreviews();
    setBeforePreview(null);
    setAfterPreview(null);
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
      render: (t) => (
        <div style={{ maxWidth: 200, whiteSpace: "normal" }}>{t || "-"}</div>
      ),
    },
    {
      title: "Before Image",
      dataIndex: "before_image",
      key: "before_image",
      render: (v) => (v ? <Image src={v} width={100} /> : "-"),
    },
    {
      title: "After Image",
      dataIndex: "after_image",
      key: "after_image",
      render: (v) => (v ? <Image src={v} width={100} /> : "-"),
    },
    { title: "Order", dataIndex: "order", key: "order" },
    { title: "Language", dataIndex: "language", key: "language" },
    {
      title: "Actions",
      key: "actions",
      render: (_, r) => (
        <Space>
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => handleView(r)}
          >
            View
          </Button>
          <Button
            icon={<EditOutlined />}
            size="small"
            onClick={() => openEdit(r)}
          >
            Edit
          </Button>
          <Button
            danger
            icon={<DeleteOutlined />}
            size="small"
            onClick={() => handleDelete(r.id)}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 12,
          gap: 12,
        }}
      >
        <h3 style={{ margin: 0 }}>Project Gallery (Before & After)</h3>
        <div style={{ flexShrink: 0 }}>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            Add
          </Button>
        </div>
      </div>

      <Table
        columns={columns}
        dataSource={list}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 10 }}
      />

      <Modal
        title={editing ? "Edit Gallery Item" : "Add Gallery Item"}
        open={isModalOpen}
        onCancel={handleModalCancel}
        footer={null}
        width={600}
      >
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item name="title" label="Title">
            <Input placeholder="Enter title (optional)" />
          </Form.Item>

          <Form.Item
            label="Before Image"
            required={!editing}
            rules={
              !editing
                ? [{ required: true, message: "Before image is required" }]
                : []
            }
          >
            <Upload
              beforeUpload={beforeUploadBefore}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Choose Before Image</Button>
            </Upload>
            {beforePreview && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={beforePreview}
                  alt="before preview"
                  style={{ maxWidth: 200, borderRadius: 4 }}
                />
              </div>
            )}
          </Form.Item>

          <Form.Item
            label="After Image"
            required={!editing}
            rules={
              !editing
                ? [{ required: true, message: "After image is required" }]
                : []
            }
          >
            <Upload
              beforeUpload={beforeUploadAfter}
              showUploadList={false}
              accept="image/*"
            >
              <Button icon={<UploadOutlined />}>Choose After Image</Button>
            </Upload>
            {afterPreview && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={afterPreview}
                  alt="after preview"
                  style={{ maxWidth: 200, borderRadius: 4 }}
                />
              </div>
            )}
          </Form.Item>

          <Form.Item name="order" label="Order">
            <InputNumber
              style={{ width: "100%" }}
              placeholder="Display order (optional)"
            />
          </Form.Item>

          <Form.Item name="language" label="Language" initialValue="Marathi">
            <Select>
              <Select.Option value="English">English</Select.Option>
              <Select.Option value="Marathi">Marathi</Select.Option>
              <Select.Option value="Hindi">Hindi</Select.Option>
            </Select>
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" block loading={loading}>
              {editing ? "Update" : "Create"}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Gallery Item Details"
        open={isViewOpen}
        onCancel={() => setIsViewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setIsViewOpen(false)}>
            Close
          </Button>,
        ]}
        width={700}
      >
        {viewing && (
          <div>
            <p>
              <strong>Title:</strong> {viewing.title || "-"}
            </p>
            <p>
              <strong>Order:</strong> {viewing.order}
            </p>
            <p>
              <strong>Language:</strong> {viewing.language || "-"}
            </p>
            <div style={{ display: "flex", gap: 16, marginTop: 16 }}>
              <div style={{ flex: 1 }}>
                <p>
                  <strong>Before:</strong>
                </p>
                {viewing.before_image ? (
                  <Image
                    src={viewing.before_image}
                    style={{ maxWidth: "100%", borderRadius: 8 }}
                  />
                ) : (
                  "-"
                )}
              </div>
              <div style={{ flex: 1 }}>
                <p>
                  <strong>After:</strong>
                </p>
                {viewing.after_image ? (
                  <Image
                    src={viewing.after_image}
                    style={{ maxWidth: "100%", borderRadius: 8 }}
                  />
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default ProjectGallery;
